import styled from 'styled-components';
import Picture from '../Picture';
import Text from '../Text';
import Flex from '../Flex';
import useAppSelector from 'hooks/useAppSelector';
import { selectHero, selectShowHero } from 'store/reducers/shopSlice';
import Link from '../Link';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';

const Hero = () => {
    const hero = useAppSelector(selectHero);
    const showHero = useAppSelector(selectShowHero);

    if (!showHero) return null;
  
    return (
        <StyledHero>
            <HeroMedia height="" hasContent>
                <span>
                    <Picture scaling={true} imageUrl={hero?.heroImageUrl || ''} />
                </span>
            </HeroMedia>
            <HeroInner>
                <HeroContent $textColor={hero?.heroTextColor ?? undefined}>
                    <Header>{hero?.heroHeading}</Header>
                    <Text style={{ color: 'inherit' }} className="mb-3">
                        {hero?.heroText}
                    </Text>

                    <Flex $wrap $gap={12}>
                        {hero?.heroLinks?.map((link, index) => (
                            <StyledLink icon={faChevronRight} to={link.url ?? ''} key={index} bgColor="fillWhite">
                                {link.text}
                            </StyledLink>
                        ))}
                    </Flex>
                </HeroContent>
            </HeroInner>
        </StyledHero>
    );
};

const StyledHero = styled.div<{ twoColumns?: boolean }>`
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    &::before {
        content: '';
        width: 100%;
        max-width: ${({ twoColumns }) => (twoColumns ? '1440' : '3500')}px;
        margin: 0 auto;
        grid-column: 1;
        grid-row: 1;
    }

    ${({ theme }) => theme.media.lg} {
        grid-template-columns: ${({ twoColumns }) => (twoColumns ? 'repeat(3, 1fr)' : '1fr')};
        ${({ twoColumns }) => (twoColumns ? 'min-height:400px;' : '')}
    }
`;
const HeroMedia = styled.div<{ noMaxHeight?: boolean; hasContent?: boolean; twoColumns?: boolean; height?: string }>`
    grid-column: 1;
    grid-row: 1;

    ${({ noMaxHeight }) =>
        !noMaxHeight &&
        `
    min-height: 350px;
  `}

    overflow: hidden;
    background: ${({ theme }) => theme.colors.fillMedium};
    ${({ hasContent, twoColumns }) =>
        hasContent &&
        `
    > *::before {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: rgba(0, 0, 0, 0.3);
      content: ${twoColumns ? '' : "''"};
      pointer-events: none;
    }
    `}
    img {
        width: 100%;
        height: 100%;

        ${({ noMaxHeight }) =>
            !noMaxHeight &&
            `
      object-fit: cover;
      min-height: 100%;
    `}
    }
    ${({ theme }) => theme.media.lg} {
        grid-column: ${({ twoColumns }) => (twoColumns ? '2 / -1' : '1fr')};

        ${({ noMaxHeight, height }) =>
            !noMaxHeight &&
            (!height
                ? 'max-height: 700px;'
                : `${(() => {
                      switch (height) {
                          case 'none':
                              return 'max-height: none;';
                          default:
                              return `max-height: ${height}; height: ${height};`;
                      }
                  })()}`)}
    }
`;
const HeroInner = styled.div<{ contentVerticalAlign?: string; contentHorizontalAlign?: string; twoColumns?: boolean }>`
    margin-left: auto;
    margin-right: auto;
    grid-row: 1;
    grid-column: 1;
    padding: 120px 16px 0 40px;
    width: 100%;
    z-index: 0;

    ${({ twoColumns, theme }) => (twoColumns ? '' : `max-width:${theme.breakpoints.xl}px;`)}

    ${({ theme }) => theme.media.md} {
        padding-left: 60px;
    }

    ${({ theme }) => theme.media.lg} {
        padding-left: 120px;
    }
    ${({ theme }) => theme.media.xl} {
        padding-left: 60px;
    }

    display: flex;
    height: 100%;
    ${({ contentVerticalAlign }) => {
        switch (contentVerticalAlign) {
            case 'top':
                return 'align-items: start;';
            case 'bottom':
                return 'align-items: end;';
            default:
                return 'align-items: center;';
        }
    }}

    ${({ contentHorizontalAlign }) => {
        switch (contentHorizontalAlign) {
            case 'content-middle':
                return 'justify-content: center;';
            case 'content-right':
                return 'justify-content: flex-end;';
            default:
                return 'justify-content: flex-start;';
        }
    }}
`;

const HeroContent = styled.div<{ textAlign?: string; $textColor?: string; twoColumns?: boolean }>`
    word-break: break-word;
    color: ${({ $textColor, theme }) => $textColor ?? theme.colors.fillWhite};
    margin: 0;
    z-index: 1;
    p {
        font-size: 16px;
        margin-bottom: 2px;
        color: inherit;
    }

    ${({ theme }) => theme.media.lg} {
        max-width: ${({ theme }) => theme.breakpoints.sm}px;
        width: 100%;

        p {
            font-size: ${({ twoColumns }) => (twoColumns ? '16' : '18')}px;
            color: ${({ twoColumns, theme, color }) => (twoColumns ? theme.colors.text : color)};
        }

        ${({ twoColumns, theme }) =>
            twoColumns &&
            `
        color: ${theme.colors.text};

        p {
          color: ${theme.colors.text};
          font-size: 16px;
        }
      `}
    }

    ${({ textAlign }) => {
        switch (textAlign) {
            case 'content-right':
                return 'text-align: end;';
            case 'content-middle':
                return 'text-align: center;';
            default:
                return 'text-align: start;';
        }
    }}
`;

const Header = styled.h3<{ twoColumns?: boolean }>`
    line-height: 1;
    word-break: break-word;
    ${({ theme }) => theme.media.lg} {
        font-size: 48px;
    }
`;

const StyledLink = styled(Link)`
    border: 2px solid ${({ theme }) => theme.colors.fillWhite};
    width: fit-content;
    color: ${({ theme }) => theme.colors.fillWhite} !important;
    border-radius: 30px;
    padding: 10px 20px;
    font-size: 16px;
    margin-right: 10px;
    margin-bottom: 10px;
    text-decoration: none;

    &&:hover,
    &&:focus {
        color: ${({ theme }) => theme.colors.text} !important;
        background: ${({ theme }) => theme.colors.fillWhite};
    }
`;

export default Hero;
